import { Pipe, PipeTransform } from '@angular/core';
import { CultureService } from './culture.service';
import { formatLabel } from './culture.config';

/**
 * A label (Multilang_Culture_Spec.md 5.2): {{ 'add-to-cart' | t:'Add to cart' }}, {{ 'items-in-cart' | t:'{n} items':{ n: count } }}.
 * The key names the meaning, the English is written next to it, and a page shows the English whenever its language has
 * no text for the key. Pure: a page's language never changes while it is open.
 */
@Pipe({ name: 't' })
export class TranslatePipe implements PipeTransform {
    constructor(private culture: CultureService) { }

    transform(key: string, fallback: string, params?: { [name: string]: any }): string {
        return this.culture.t(key, fallback, params);
    }
}

/**
 * An HTML label, for [innerHTML] only: [innerHTML]="'checkout-terms' | tHtml:'Read the <a href=&quot;/terms&quot;>terms</a>'".
 * The server allows a few tags in it (CultureLabelRules); Angular sanitises whatever is bound to innerHTML besides.
 */
@Pipe({ name: 'tHtml' })
export class TranslateHtmlPipe implements PipeTransform {
    constructor(private culture: CultureService) { }

    transform(key: string, fallback: string, params?: { [name: string]: any }): string {
        return this.culture.t(key, fallback, params);
    }
}

/**
 * A sentence with one element inside it - a link, a bold button name - as one label, the element where the translation
 * puts it (rule 6: never fragments glued in English order):
 * <ng-container *ngIf="('agree-terms' | tAround:'I agree to the {terms}':'terms') as p">{{ p[0] }}<a ...>terms</a>{{ p[1] }}</ng-container>.
 * The text before and after {slot}; with no {slot} in it, all of it before. Two or more elements: the slots in the
 * order the template has them, ['api', 'json'] -> [before, between, after]; a translation that puts them in another
 * order shows the English. Plain data around them fills {placeholders} as with t: tAround:'Up to {amount} on {link}':'link':{ amount: x }.
 */
@Pipe({ name: 'tAround' })
export class TranslateAroundPipe implements PipeTransform {
    constructor(private culture: CultureService) { }

    transform(key: string, fallback: string, slot: string | string[], params?: { [name: string]: any }): string[] {
        const slots = Array.isArray(slot) ? slot : [slot];
        const cut = (text: string): string[] => {
            const parts: string[] = [];
            let rest = text;
            for (const name of slots) {
                const mark = '{' + name + '}';
                const at = rest.indexOf(mark);
                if (at < 0) { return null; }
                parts.push(rest.substring(0, at));
                rest = rest.substring(at + mark.length);
            }
            parts.push(rest);
            return parts;
        };
        const text = this.culture.t(key, fallback, params);
        return cut(text) || cut(formatLabel(fallback, params)) || [text].concat(slots.map(() => ''));
    }
}

/** A category's name from the server in the page's language: {{ category.name | tCategory }} (CultureService.category). */
@Pipe({ name: 'tCategory' })
export class TranslateCategoryPipe implements PipeTransform {
    constructor(private culture: CultureService) { }

    transform(name: string): string {
        return this.culture.category(name);
    }
}

/** An app link that keeps the visitor in the page's language (5.3): [routerLink]="'/blog' | localizeLink" is /ar/blog on an Arabic page. */
@Pipe({ name: 'localizeLink' })
export class LocalizeLinkPipe implements PipeTransform {
    constructor(private culture: CultureService) { }

    transform<T extends string | any[]>(link: T): T {
        return this.culture.link(link);
    }
}
